/*
  The customer-clip row: silent looping previews that open with sound in a dialog.

  No theme components involved, so this travels with the section.

  Points that are deliberate:

  - Autoplay only survives when the video is muted and marked playsinline, and even then a
    browser can refuse. play() returns a promise that rejects rather than throwing, so the
    rejection is swallowed on purpose - a blocked preview should sit on its poster, not put
    an unhandled rejection in the console on every page view.

  - Previews are only started when they are actually on screen, and paused when they leave.
    Four videos looping off-screen cost battery for nothing.

  - Under prefers-reduced-motion nothing plays by itself at all. The poster stands in, and
    the dialog still works on click.

  - The dialog holds one video element that is re-pointed at whichever clip was clicked,
    rather than one element per clip. Sound belongs to the dialog, so only one can ever be
    audible and there is nothing to keep in sync.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-oralux-clips]';

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function play(video) {
    if (!video || reduced()) return;
    var attempt = video.play();
    /* Rejected autoplay is an expected outcome, not an error worth surfacing. */
    if (attempt && attempt.catch) attempt.catch(function () {});
  }

  function buildSlider(root) {
    var el = root.querySelector('[data-oralux-clips-slider]');
    if (!el || !window.Swiper) return;
    if (el.swiper) el.swiper.destroy(true, true);

    /*
      Counted per-view rather than 'auto', because the design gives every slide an equal share
      of the row. With 'auto' the width has to come from CSS, and a fixed width overflows any
      column narrower than n x width + gaps.
    */
    var perView = parseFloat(root.getAttribute('data-per-view')) || 4;
    var perViewMobile = parseFloat(root.getAttribute('data-per-view-mobile')) || 3;

    new window.Swiper(el, {
      slidesPerView: perViewMobile,
      /* 750 is the breakpoint the section's own media queries use. */
      breakpoints: { 750: { slidesPerView: perView } },
      spaceBetween: parseInt(root.getAttribute('data-gap'), 10) || 8,
      freeMode: { enabled: true },
      a11y: { enabled: true },
      navigation: {
        prevEl: root.querySelector('[data-oralux-clips-prev]'),
        nextEl: root.querySelector('[data-oralux-clips-next]'),
      },
      scrollbar: { el: root.querySelector('.swiper-scrollbar'), draggable: true },
    });
  }

  function initOne(root) {
    if (root.hasAttribute('data-oralux-ready')) return;
    root.setAttribute('data-oralux-ready', '');

    if (window.Swiper) buildSlider(root);
    else {
      var tries = 0;
      var timer = setInterval(function () {
        if (window.Swiper) {
          clearInterval(timer);
          buildSlider(root);
        } else if (++tries >= 60) clearInterval(timer);
      }, 50);
    }

    var dialog = root.querySelector('[data-oralux-clip-dialog]');
    var player = dialog ? dialog.querySelector('video') : null;

    /* Play only what is on screen. */
    if ('IntersectionObserver' in window) {
      var seen = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) play(video);
            else if (!video.paused) video.pause();
          });
        },
        { threshold: 0.25 }
      );
      root.querySelectorAll('[data-oralux-clip] video').forEach(function (video) {
        seen.observe(video);
      });
    } else {
      root.querySelectorAll('[data-oralux-clip] video').forEach(play);
    }

    if (!dialog || !player) return;

    function previews(action) {
      root.querySelectorAll('[data-oralux-clip] video').forEach(function (video) {
        if (action === 'pause') video.pause();
        else play(video);
      });
    }

    function close() {
      player.pause();
      player.removeAttribute('src');
      player.load();
      if (dialog.open) dialog.close();
      previews('play');
    }

    root.addEventListener('click', function (event) {
      var clip = event.target.closest('[data-oralux-clip]');
      if (!clip || !root.contains(clip) || clip.closest('[data-oralux-clip-dialog]')) return;

      var source = clip.dataset.oraluxSrc;
      if (!source) return;
      event.preventDefault();

      player.src = source;
      player.currentTime = 0;
      player.muted = false;
      player.controls = true;
      /* Silent loops carrying on behind the dialog is exactly the sort of thing nobody
         notices in review and everybody hears once one of them has sound. */
      previews('pause');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      play(player);
    });

    dialog.addEventListener('click', function (event) {
      /* A click on the backdrop lands on the dialog itself, never on its content. */
      if (event.target === dialog || event.target.closest('[data-oralux-clip-close]')) close();
    });

    dialog.addEventListener('close', close);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
